/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { AccountTlaStatus, CrmEntityManager, EntityName } from "@gamedev-web/one-gaming-crm";

import {
  CrmGameConceptStatus,
  CrmGameConceptStatusOrder2,
  CrmGameConceptSubStatus,
} from "../models/crmStatus";
import { AaaGameConceptData, IdXboxGameConceptData } from "../models/forms";
import { getGameConceptsList } from "../redux/selectors";
import {
  getGameConceptStatusFromCrmApprovalStatus,
  mapConceptCrmToForms,
} from "../server/crm/api/mappers/gameConcept/mapConceptCrmToForms";
import {
  AaaGameConceptDataSet,
  GameConceptState,
  IdXboxGameConceptDataSet,
} from "../server/crm/api/models/forms";
import { CrmGamesApprovalStatusesResponse } from "../server/crm/dataService/models/customCrmQueryModels";

export const isGameConceptHardRejected = (
  gameConceptStatus: CrmGameConceptStatus | undefined,
  gameConceptSubStatus: CrmGameConceptSubStatus | undefined
) =>
  gameConceptStatus === CrmGameConceptStatus.Rejected &&
  gameConceptSubStatus !== CrmGameConceptSubStatus.RejectedSoftAlreadyHaveDevToken &&
  gameConceptSubStatus !== CrmGameConceptSubStatus.RejectedSoftGeneral;

export const isGameConceptSoftRejected = (
  gameConceptStatus: CrmGameConceptStatus | undefined,
  gameConceptSubStatus: CrmGameConceptSubStatus | undefined
) =>
  gameConceptStatus === CrmGameConceptStatus.Rejected &&
  (gameConceptSubStatus === CrmGameConceptSubStatus.RejectedSoftAlreadyHaveDevToken ||
    gameConceptSubStatus === CrmGameConceptSubStatus.RejectedSoftGeneral);

export const pickGameConceptForDashboard = (
  gameConcepts: IdXboxGameConceptDataSet | AaaGameConceptDataSet,
  selectedGameConceptId: string | undefined
): ((IdXboxGameConceptData | AaaGameConceptData) & GameConceptState) | undefined => {
  // If the URL points at a concept the loaded set doesn't contain (e.g. a
  // freshly-minted draft id whose row hasn't been written yet, or a stale id),
  // there's nothing to render on the dashboard.
  if (!selectedGameConceptId) {
    return undefined;
  }

  return gameConcepts[selectedGameConceptId] ?? undefined;
};

export type GameConceptApplication = {
  id: string;
  gameConceptInfo: (IdXboxGameConceptData | AaaGameConceptData) & GameConceptState;
};

export type CategorizedGameConcepts = {
  incompleteApplications: GameConceptApplication[];
  incompleteReadOnlyApplications: GameConceptApplication[];
  completeApplications: GameConceptApplication[];
  rejectedApplications: GameConceptApplication[];
};

export const useCategorizedGameConcepts = (): CategorizedGameConcepts | undefined => {
  const crmGameConceptsList = useSelector(getGameConceptsList);
  const [applications, setApplications] = useState<CategorizedGameConcepts | undefined>(undefined);

  useEffect(() => {
    const incomplete = [];
    const incompleteReadOnly = [];
    const complete = [];
    const rejected = [];

    for (const key in crmGameConceptsList) {
      const gameConcept = crmGameConceptsList[key];
      const applicationToStore = { id: key, gameConceptInfo: gameConcept };
      switch (gameConcept.state.status) {
        case CrmGameConceptStatus.Accepted:
          complete.push(applicationToStore);
          break;
        case CrmGameConceptStatus.Rejected:
          rejected.push(applicationToStore);
          break;
        case CrmGameConceptStatus.UnderReview:
        case CrmGameConceptStatus.AdditionalContractsSent:
        case CrmGameConceptStatus.OnHold:
          incompleteReadOnly.push(applicationToStore);
          break;
        case CrmGameConceptStatus.Unset:
        case CrmGameConceptStatus.MoreInfoNeeded:
          incomplete.push(applicationToStore);
          break;
      }
    }
    setApplications({
      incompleteApplications: incomplete,
      incompleteReadOnlyApplications: incompleteReadOnly,
      completeApplications: complete,
      rejectedApplications: rejected,
    });
  }, [crmGameConceptsList]);

  return applications;
};

export const usePaginatedGameConcepts = (): GameConceptApplication[] | undefined => {
  const crmGameConceptsList = useSelector(getGameConceptsList);

  return useMemo(
    () =>
      crmGameConceptsList
        ? Object.entries(crmGameConceptsList).map(([id, gameConceptInfo]) => ({
            id,
            gameConceptInfo,
          }))
        : undefined,
    [crmGameConceptsList]
  );
};

export enum GameConceptFilter {
  Draft = "Draft",
  UnderReview = "UnderReview",
  Approved = "Approved",
  Rejected = "Rejected",
  NeedMoreInfo = "NeedMoreInfo",
  OnHold = "OnHold",
}

export enum GameConceptTab {
  All = "All",
  Draft = "Draft",
  Submitted = "Submitted",
}

export const GameConceptFiltersForTab: { [key in GameConceptTab]: GameConceptFilter[] } = {
  [GameConceptTab.Draft]: [GameConceptFilter.Draft],
  [GameConceptTab.Submitted]: [
    GameConceptFilter.UnderReview,
    GameConceptFilter.Approved,
    GameConceptFilter.NeedMoreInfo,
    GameConceptFilter.Rejected,
    GameConceptFilter.OnHold,
  ],
  [GameConceptTab.All]: [
    GameConceptFilter.UnderReview,
    GameConceptFilter.Approved,
    GameConceptFilter.NeedMoreInfo,
    GameConceptFilter.Rejected,
    GameConceptFilter.OnHold,
    GameConceptFilter.Draft,
  ],
};

export const getMostAdvancedGameConcept = (
  gameConceptsList: IdXboxGameConceptDataSet
): (IdXboxGameConceptData & GameConceptState) | undefined => {
  let highestStatusNumericValue = -1;
  let mostAdvancedGameConcept: (IdXboxGameConceptData & GameConceptState) | undefined;

  for (const key in gameConceptsList) {
    const gameConcept = gameConceptsList[key];
    const gameConceptStatus = gameConcept.state.status;
    const statusNumericValue = CrmGameConceptStatusOrder2[gameConceptStatus];

    if (statusNumericValue > highestStatusNumericValue) {
      highestStatusNumericValue = CrmGameConceptStatusOrder2[gameConceptStatus];
      mostAdvancedGameConcept = gameConcept;
    }
  }

  return mostAdvancedGameConcept;
};

export type BaseIdXboxGameConceptsMetadata = {
  isAtLeastOneGameConceptAccepted: boolean;
  highestCrmGameConceptStatus: CrmGameConceptStatus | undefined;
};

export type IdXboxGameConceptsMetadata = {
  gameConcepts: IdXboxGameConceptDataSet;
  earliestAcceptedGameConcept: (IdXboxGameConceptData & GameConceptState) | undefined;
} & BaseIdXboxGameConceptsMetadata;

export const processDataFromGameConcepts = (
  crmGameConcepts: CrmEntityManager<EntityName.gaming_gameconcept>[]
): IdXboxGameConceptsMetadata => {
  let isAtLeastOneGameConceptAccepted = false;
  let earliestAcceptedGameConcept: (IdXboxGameConceptData & GameConceptState) | undefined;
  let highestCrmGameConceptStatus: CrmGameConceptStatus | undefined;
  let highestStatusNumericValue = -1;
  const gameConcepts = crmGameConcepts.reduce((result, concept) => {
    if (!concept.get("gaming_visibletopartner")) {
      return result;
    }

    const gameConcept = mapConceptCrmToForms(concept);
    const gameConceptState = gameConcept.state;
    const gameConceptStatus = gameConceptState.status;

    if (gameConceptStatus === CrmGameConceptStatus.Accepted) {
      isAtLeastOneGameConceptAccepted = true;
      if (!earliestAcceptedGameConcept && gameConcept.state.acceptedDate) {
        earliestAcceptedGameConcept = gameConcept;
      } else if (
        gameConcept.state.acceptedDate &&
        earliestAcceptedGameConcept?.state.acceptedDate &&
        gameConcept.state.acceptedDate < earliestAcceptedGameConcept.state.acceptedDate
      ) {
        earliestAcceptedGameConcept = gameConcept;
      }
    }

    const statusNumericValue = CrmGameConceptStatusOrder2[gameConceptStatus];
    if (statusNumericValue > highestStatusNumericValue) {
      highestStatusNumericValue = CrmGameConceptStatusOrder2[gameConceptStatus];
      highestCrmGameConceptStatus = gameConceptStatus;
    }

    result[concept.get("gaming_gameconceptid")] = gameConcept;
    return result;
  }, {});
  return {
    gameConcepts,
    isAtLeastOneGameConceptAccepted,
    earliestAcceptedGameConcept,
    highestCrmGameConceptStatus,
  };
};

export const processDataFromGameConceptsV2 = (
  approvalStatusesFound: CrmGamesApprovalStatusesResponse,
  account: CrmEntityManager<EntityName.account>
): BaseIdXboxGameConceptsMetadata => {
  let isAtLeastOneGameConceptAccepted = false;
  let highestCrmGameConceptStatus: CrmGameConceptStatus | undefined;
  let highestStatusNumericValue = -1;
  const isGeneralTlaActiveOnAccount = account.get("gaming_tlastatus") === AccountTlaStatus.Active;

  for (const approvalStatus of approvalStatusesFound) {
    if (!approvalStatus?.ggp_approvalstatus) {
      continue;
    }

    const gameConceptStatus = getGameConceptStatusFromCrmApprovalStatus(
      approvalStatus.ggp_approvalstatus,
      isGeneralTlaActiveOnAccount,
      true
    );

    if (gameConceptStatus === CrmGameConceptStatus.Accepted) {
      isAtLeastOneGameConceptAccepted = true;
    }

    const statusNumericValue = CrmGameConceptStatusOrder2[gameConceptStatus];
    if (statusNumericValue > highestStatusNumericValue) {
      highestStatusNumericValue = CrmGameConceptStatusOrder2[gameConceptStatus];
      highestCrmGameConceptStatus = gameConceptStatus;
    }
  }
  return {
    isAtLeastOneGameConceptAccepted,
    highestCrmGameConceptStatus,
  };
};

export const mapCrmGameConceptsToForms = (
  crmGameConcepts: CrmEntityManager<EntityName.gaming_gameconcept>[]
): IdXboxGameConceptDataSet =>
  crmGameConcepts.reduce((result: IdXboxGameConceptDataSet, concept) => {
    if (!concept.get("gaming_visibletopartner")) {
      return result;
    }

    result[concept.get("gaming_gameconceptid")] = mapConceptCrmToForms(concept);
    return result;
  }, {});
